import { useCallback, useEffect, useRef, useState } from "react";

import type { Cluster } from "../components/ClusterCard.js";
import { Icon } from "../components/Icon.js";
import { ApiError } from "../api/client.js";
import { useI18n } from "../i18n.js";
import { useSession } from "../session.js";

/**
 * La calibrazione a quattro angoli, portata da `CalibrationWizard.qml`.
 *
 * Lega la planimetria alla testa PTZ: si clicca un angolo sull'immagine e si
 * scrive il pan/tilt a cui la testa guarda quel punto. Quattro coppie
 * bastano a passare da un punto sulla pianta a una posizione della testa.
 *
 * L'ordine e' fisso — alto sinistra, alto destra, basso sinistra, basso
 * destra — e il colore del punto attivo e' l'unica cosa che dice all'operatore
 * quale sta segnando. Il clic si converte sul rettangolo DIPINTO
 * dell'immagine, non sul riquadro che la contiene: con il "contain" le bande
 * ai lati sposterebbero ogni coordinata.
 *
 * Senza planimetria non c'e' niente da calibrare, e la schermata lo dice
 * invece di mostrare un riquadro vuoto.
 */

interface Point {
  x: number | null;
  y: number | null;
  pan: string;
  tilt: string;
}

const EMPTY: Point = { x: null, y: null, pan: "", tilt: "" };

/** I sedici campi `cal_*`, nell'ordine dei quattro angoli. */
const FIELDS: [string, string, string, string][] = [
  ["calTopLeftX", "calTopLeftY", "calTopLeftPan", "calTopLeftTilt"],
  ["calTopRightX", "calTopRightY", "calTopRightPan", "calTopRightTilt"],
  ["calBottomLeftX", "calBottomLeftY", "calBottomLeftPan", "calBottomLeftTilt"],
  ["calBottomRightX", "calBottomRightY", "calBottomRightPan", "calBottomRightTilt"],
];

const LABEL_KEYS = ["calTL", "calTR", "calBL", "calBR"];
const COLORS = ["#22c55e", "#3b82f6", "#eab308", "#ef4444"];

export function CalibrationWizard({
  cluster,
  onBack,
}: {
  cluster: Cluster;
  onBack: () => void;
}) {
  const { t } = useI18n();
  const { api } = useSession();

  const [points, setPoints] = useState<Point[]>([EMPTY, EMPTY, EMPTY, EMPTY]);
  const [active, setActive] = useState(0);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | undefined>();
  const [saving, setSaving] = useState(false);
  /** Il rettangolo **dipinto** dell'immagine dentro il suo riquadro, in pixel.
   *  Con `object-fit: contain` una planimetria 4:3 in un riquadro 16:9 lascia
   *  due bande ai lati, e un pallino piazzato in percentuale sul riquadro
   *  finisce spostato di tutta la banda: il `.qml` infatti somma
   *  `(width - paintedWidth)/2`. */
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [box, setBox] = useState({ left: 0, top: 0, w: 0, h: 0 });

  const measure = useCallback(() => {
    const img = imgRef.current;
    if (!img || !img.naturalWidth || !img.naturalHeight) return;
    const r = img.getBoundingClientRect();
    const ar = img.naturalWidth / img.naturalHeight;
    const w = ar > r.width / r.height ? r.width : r.height * ar;
    const h = ar > r.width / r.height ? r.width / ar : r.height;
    setBox({ left: (r.width - w) / 2, top: (r.height - h) / 2, w, h });
  }, []);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return undefined;
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(img);
    return () => ro.disconnect();
  }, [measure, cluster.plantImageUrl]);

  // I punti gia' calibrati si rileggono dal cluster: ricominciare da zero a
  // ogni apertura vorrebbe dire rifare la misura per cambiare un angolo.
  const load = useCallback(async () => {
    try {
      const c = await api.call<Record<string, unknown>>("clusters.get", { id: cluster.id });
      const pts = FIELDS.map(([xk, yk, pk, tk]) => {
        const x = c[xk];
        const y = c[yk];
        return {
          x: x === null || x === undefined ? null : Number(x),
          y: y === null || y === undefined ? null : Number(y),
          pan: c[pk] === null || c[pk] === undefined ? "" : String(c[pk]),
          tilt: c[tk] === null || c[tk] === undefined ? "" : String(c[tk]),
        };
      });
      if (pts.some((p) => p.x !== null && p.y !== null)) setPoints(pts);
    } catch {
      /* niente da rileggere: si parte dai quattro punti vuoti */
    }
  }, [api, cluster.id]);

  useEffect(() => void load(), [load]);

  const place = (e: React.MouseEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const r = img.getBoundingClientRect();
    const ar = img.naturalWidth / img.naturalHeight;
    const boxAr = r.width / r.height;
    const w = ar > boxAr ? r.width : r.height * ar;
    const h = ar > boxAr ? r.width / ar : r.height;
    const x = (e.clientX - r.left - (r.width - w) / 2) / w;
    const y = (e.clientY - r.top - (r.height - h) / 2) / h;
    if (x < 0 || x > 1 || y < 0 || y > 1) return;
    setPoints((cur) => cur.map((p, i) => (i === active ? { ...p, x, y } : p)));
    setMessage(undefined);
    if (active < 3) setActive(active + 1);
  };

  const setField = (i: number, key: "pan" | "tilt", v: string) =>
    setPoints((cur) => cur.map((p, k) => (k === i ? { ...p, [key]: v } : p)));

  const reset = () => {
    setPoints([EMPTY, EMPTY, EMPTY, EMPTY]);
    setActive(0);
    setMessage(undefined);
  };

  const save = async () => {
    for (const [i, p] of points.entries()) {
      if (p.x === null || p.y === null) {
        setMessage({ ok: false, text: `Punto ${i + 1} non posizionato` });
        return;
      }
      if (p.pan === "" || p.tilt === "") {
        setMessage({ ok: false, text: `Pan/Tilt mancanti per punto ${i + 1}` });
        return;
      }
      if (Number.isNaN(Number(p.pan)) || Number.isNaN(Number(p.tilt))) {
        setMessage({ ok: false, text: `Valori Pan/Tilt non validi per punto ${i + 1}` });
        return;
      }
    }
    setSaving(true);
    const body: Record<string, unknown> = { id: cluster.id };
    FIELDS.forEach(([xk, yk, pk, tk], i) => {
      const p = points[i]!;
      body[xk] = p.x;
      body[yk] = p.y;
      body[pk] = Number(p.pan);
      body[tk] = Number(p.tilt);
    });
    try {
      await api.call("clusters.update", body);
      setMessage({ ok: true, text: "Calibrazione salvata con successo" });
    } catch (e) {
      setMessage({ ok: false, text: e instanceof ApiError ? e.message : "Salvataggio fallito" });
    } finally {
      setSaving(false);
    }
  };

  if (!cluster.plantImageUrl) {
    return (
      <div className="denied">
        <Icon name="alert-triangle" size={48} />
        <h2>{t("calib.noPlantImage")}</h2>
        <p className="muted">{t("calib.configureFirst")}</p>
        <button className="btn--primary" onClick={onBack}>
          {t("calib.backToSettings")}
        </button>
      </div>
    );
  }

  return (
    <div className="calib">
      {/* L'intestazione qui **non** e' la barra a tutta finestra delle altre
          schermate: nel `.qml` sta dentro i 24 px di margine della pagina, e
          il bottone indietro e' un quadrato. */}
      <header className="calib__head">
        <button className="calib__back" onClick={onBack}>
          <Icon name="arrow-left" size={20} />
        </button>
        <div>
          <h2>{t("calib.title")}</h2>
          <span className="calib__sub">
            {cluster.name} — {cluster.areaCode ?? ""}
          </span>
        </div>
      </header>

      <div className="calib__hint">
        {t("calib.clickToPlace")}{" "}
        <b style={{ color: COLORS[active] }}>{t(LABEL_KEYS[active] ?? "calTL")}</b>
      </div>

      <div className="calib__body">
        <div className="calib__image">
          <img ref={imgRef} src={cluster.plantImageUrl} alt="" onClick={place} onLoad={measure} />
          {points.map((p, i) =>
            p.x === null || p.y === null ? null : (
              <span
                key={LABEL_KEYS[i]}
                className={`calib__dot${active === i ? " calib__dot--on" : ""}`}
                style={{
                  left: `${box.left + p.x * box.w}px`,
                  top: `${box.top + p.y * box.h}px`,
                  background: COLORS[i],
                }}
              >
                {i + 1}
              </span>
            ),
          )}
        </div>

        <div className="calib__panel">
          {points.map((p, i) => (
            <div
              key={LABEL_KEYS[i]}
              className={`calib__card${active === i ? " calib__card--on" : ""}`}
              style={active === i ? { borderColor: COLORS[i] } : undefined}
              onClick={() => setActive(i)}
            >
              <div className="calib__cardHead">
                <i style={{ background: COLORS[i] }} />
                <b>{t(LABEL_KEYS[i] ?? "calTL")}</b>
                <span className="muted small">
                  {p.x === null || p.y === null
                    ? ""
                    : `X:${(p.x * 100).toFixed(1)}% Y:${(p.y * 100).toFixed(1)}%`}
                </span>
              </div>
              <div className="cgrid">
                <label className="cfield">
                  <span>PAN</span>
                  <input value={p.pan} onChange={(e) => setField(i, "pan", e.target.value)} />
                </label>
                <label className="cfield">
                  <span>TILT</span>
                  <input value={p.tilt} onChange={(e) => setField(i, "tilt", e.target.value)} />
                </label>
              </div>
            </div>
          ))}

          {message && (
            <p className={`calib__msg calib__msg--${message.ok ? "ok" : "ko"}`}>
              <Icon name={message.ok ? "check-circle" : "alert-triangle"} size={16} />
              {message.text}
            </p>
          )}

          <div className="calib__acts">
            <button onClick={reset}>
              <Icon name="rotate-ccw" size={16} />
              Reset
            </button>
            <button className="btn--primary" disabled={saving} onClick={() => void save()}>
              <Icon name="save" size={16} />
              {saving ? "Salvataggio..." : "Salva"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
